import { Component, EventEmitter, Injector, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Dialog } from 'primeng/dialog';
import { SessionBaseComponent } from '../session.base.component';

export enum DisplayMode {
  Desktop,
  Mobile
}

export enum FileDialogMode {
  Edit,
  Download
}

@Component({
  selector: 'laas-file-upload-dialog',
  templateUrl: './file-upload-dialog.component.html',
  styleUrls: ['./file-upload-dialog.component.scss']
})
export class FileUploadDialogComponent extends SessionBaseComponent implements OnInit {

  @Input() title: string = '';
  @Input() filePermission: boolean;
  @Input() isFileDialogLoading: boolean;
  @Input() singleUploadMode: boolean;
  @Input() batchUploadMode: boolean;
  @Input() context: string;

  @Output() saveDataChanges: EventEmitter<any>;
  @Output() restoreFileData: EventEmitter<any>;

  @ViewChild('fileDialog') fileDialog!: Dialog;

  isShowFileDialog: boolean;
  fileDialogMode: FileDialogMode = FileDialogMode.Edit;
  fileList: any[] = [];

  readonly FILE_DIALOG_MODE_ENUM = FileDialogMode;

  displayMode: DisplayMode = DisplayMode.Desktop;
  readonly MOBILE_MODE_WINDOW_WIDTH = 640;
  readonly DISPLAY_MODE_ENUM = DisplayMode;
  mobileTabPanelActiveIndex: number = 0;
  isShowBigFile: boolean = false;
  indexOfGalleria: number = 0;

  newFileBase64s: any[] = [];
  deletedFileDocIds: string[] = [];

  constructor(
    injector: Injector,
    private sanitizer: DomSanitizer,
  ) {
    super(injector);
    this.filePermission = true;
    this.isFileDialogLoading = false;
    this.singleUploadMode = false;
    this.batchUploadMode = false;
    this.isShowFileDialog = false;
    this.saveDataChanges = new EventEmitter<any>();
    this.restoreFileData = new EventEmitter<any>();
    this.context = "";

    let windowResizeEventListener = this.onWindowResize.bind(this);
    window.addEventListener('resize', windowResizeEventListener);
    if (window.innerWidth <= this.MOBILE_MODE_WINDOW_WIDTH) {
      this.changeToMobileMode();
    }
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();
  }

  @Output() changeUploadMode(mode: string) {
    this.singleUploadMode = false;
    this.batchUploadMode = false;
    switch(mode) {
      case 'single':
        this.singleUploadMode = true;
        break;
      case 'batch':
        this.batchUploadMode = true;
        break;
    }
  }

  onWindowResize() {
    if (window.innerWidth <= this.MOBILE_MODE_WINDOW_WIDTH) {
      this.changeToMobileMode();
    }
    else {
      this.changeToDesktopMode();
    }
  }

  changeToMobileMode(): void {
    this.displayMode = DisplayMode.Mobile;
  }

  changeToDesktopMode(): void {
    this.displayMode = DisplayMode.Desktop;
  }

  isInDesktopMode(): boolean {
    return this.displayMode === this.DISPLAY_MODE_ENUM.Desktop;
  }

  visibleChange(visible: boolean) {
    if (!visible) {
      this.confirmationService.confirm({
        header: this.translateService.instant(this.title),
        message: this.translateService.instant('Common.Dialog.Message.File.Exit'),
        icon: 'pi pi-info-circle',
        accept: () => {
          this.onCloseDialog();
        },
        reject: () => {
          this.isShowFileDialog = true;
        }
      })
      return;
    }

    this.isShowFileDialog = visible;
    if (!this.isInDesktopMode()) {
      this.fileDialog.maximizable = false;
      this.fileDialog.maximized = false;
      this.fileDialog.maximize();
      this.fileDialog.disableModality();
    }
  }

  onCloseDialog() {
    this.isShowFileDialog = false;
    this.isFileDialogLoading = false;
    this.fileDialogMode = this.FILE_DIALOG_MODE_ENUM.Edit;
    this.fileList = [];
    this.newFileBase64s = [];
    this.deletedFileDocIds = [];
  }

  restoreFileDataEvent(): void {
    this.restoreFileData.emit();
  }

  isFileDialogInEditMode(): boolean {
    return this.fileDialogMode === this.FILE_DIALOG_MODE_ENUM.Edit;
  }

  readonly MAX_FILE_NAME_LENGTH = 200;

  async addFileEvent(fileInput: HTMLInputElement) {
    let files = fileInput.files;
    if (!files || this.isExceedMaxSize(files)) {
      fileInput.value = "";
      return;
    }

    for (let i = 0; i < files.length; i++) {
      if (this.isFileNameTooLong(files[i])) {
        this.toastService.warn(this.translateService.instant('Common.Dialog.Message.File.FileNameTooLong'));
        continue;
      }
      if (!this.isFileSelected(files[i])) {
        let fileBase64 = await this.convertFileToBase64(files[i]);
        if (fileBase64) {
          const docId = (this.newFileBase64s.length + 1).toString();
          let fileBean = this.convertToFileBean(files[i], fileBase64, docId);
          this.fileList.splice(i, 0, fileBean);
          let fileBase64Bean = this.convertToFileBase64Bean(files[i], fileBase64);
          fileBase64Bean.docId = docId;
          this.newFileBase64s.push(fileBase64Bean);
        }
      }
    }

    fileInput.value = "";
  }

  isExceedMaxSize(files: FileList): boolean{
    switch(this.context){
      case "ReceivingInstruction":
        const MAX_SIZE_MB = 50 * 1024 * 1024;
        let totalFileSize = this.fileList.reduce((total, file) => total + this.base64Size(file.fileDataUrl), 0);
        let newFileSize = Array.from(files).reduce((total, file) => total + file.size, 0);

        if(totalFileSize + newFileSize > MAX_SIZE_MB){
          this.toastService.warn(this.translateService.instant('ReceivingInstruction.Dialog.File.FileSizeLimit'));
          return true;
        }
      break;
    }
    return false;
  }

  base64Size(base64: string): number {
    let padding = 0;
    if (base64.endsWith('==')) {
      padding = 2;
    } else if (base64.endsWith('=')) {
      padding = 1;
    }
    const base64Length = base64.length - padding;
    return (base64Length * 3) / 4;
  }

  isFileNameTooLong(file: File): boolean {
    return file.name.length > this.MAX_FILE_NAME_LENGTH;
  }

  isFileSelected(file: File): boolean {
    for (let fileItem of this.fileList) {
      let fileName = file.name.split('.')
      if ((fileItem.fileName! + fileItem.fileType) === (fileName[0] + fileName[1] + file.type)) {
        return true;
      }
    }
    return false;
  }

  async convertFileToBase64(file: File): Promise<string | null> {
    return new Promise((resolve, reject) => {
      let fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        let result = fileReader.result as string;
        if (result) {
          resolve(result.split(',')[1]);
        }
      }
      fileReader.onerror = () => reject(null)
    })
  }

  convertToFileBean(file: File, fileBase64: string, docId: string) {
    let fileNameArray = file.name.split('.');
    let fileExtension: string | null = fileNameArray.pop()!;
    let fileName = fileNameArray.join('.')!;

    if (fileName.length == 0) {
      fileName = fileExtension;
      fileExtension = null;
    }

    let fileBean = {
      docId: docId,
      fileName: fileName,
      fileExtension: fileExtension,
      fileType: file.type,
      fileDataUrl: `data:${file.type};base64,${fileBase64}`
    };
    return fileBean;
  }

  convertToFileBase64Bean(file: File, fileBase64: string): any {
    let fileNameArray = file.name.split('.');
    let fileExtension: string | null = fileNameArray.pop()!;
    let fileName = fileNameArray.join('.')!;

    if (fileName.length == 0) {
      fileName = fileExtension;
      fileExtension = null;
    }

    let fileBase64Bean = {
      fileName: fileName,
      fileExtension: fileExtension,
      fileType: file.type,
      fileBase64s: fileBase64
    };
    return fileBase64Bean;
  }

  isImageTypeFile(fileType: string): boolean {
    return this.getTypeClass(fileType) === this.getTypeClass("image/*")
  }

  getTypeClass(fileType: string): string {
    return fileType.substring(0, fileType.indexOf('/'));
  }

  transformToSafeUrl(url: string | null) {
    return url ? this.sanitizer.bypassSecurityTrustUrl(url) : '';
  }

  onFileClick(index: number) {
    if (this.isInDesktopMode()) {
      // Because Galleria follow the dialog's size
      // Below make galleria in full screen
      // but when close the galleria, restore the dialog to its original size
      let originalMaximized = this.fileDialog.maximized;
      this.fileDialog.maximized = false;
      this.fileDialog.maximize();
      this.fileDialog.disableModality();
      this.fileDialog.maximized = !originalMaximized;
    }

    this.isShowBigFile = true;
    this.indexOfGalleria = index;
  }

  deleteFileEvent(docId: string): void {
    const fileListIndex = this.fileList.findIndex(file => file.docId === docId);
    if (fileListIndex > -1) {
      this.fileList.splice(fileListIndex, 1);
    }
    const newFileIndex = this.newFileBase64s.findIndex(file => file.docId === docId);
    if (newFileIndex > -1) {
      this.newFileBase64s.splice(newFileIndex, 1);
    }
    if (docId && docId.length === 36) {
      this.deletedFileDocIds.push(docId);
    }
  }

  visibleChangeOfGalleria() {
    if (this.isInDesktopMode()) {
      // Restore the dialog to its original size
      this.fileDialog.maximize();
      this.fileDialog.disableModality();
    }
  }

  isAbleToSaveFileChange() {
    return (this.newFileBase64s != null && this.newFileBase64s.length > 0)
      || (this.deletedFileDocIds != null && this.deletedFileDocIds.length > 0)
  }

  onSaveBtnClickOnFileDialog() {
    this.confirmationService.confirm({
      header: this.translateService.instant(this.title),
      message: this.translateService.instant('Common.Dialog.Message.File.Save'),
      icon: 'pi pi-info-circle',
      accept: () => {
        this.isFileDialogLoading = true;
        let req = {
          userName: this.userService.userInfo.userName,
          operationDate: new Date(),
          newFiles: this.newFileBase64s,
          deletedFileDocIds: this.deletedFileDocIds
        }
        this.saveDataChanges.emit(req);
      }
    });
  }
}
